import { rosterComputedCell, rosterClassIcons, rosterThumbnailHtml } from './creature-cells';
import { parseCampaignRowsResponse } from '../shared/campaign-list-rows';
import type { CatalogListConfig } from './types';
import { campaignRosterView, type CampaignRosterPlayer as CampaignSessionPlayerRow, type CampaignRosterView as CatalogCampaignSessionPlayersResponse } from './campaign-roster-rows';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function wireSessionControls(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-ft-session-action]:not([data-ft-mounted="1"])').forEach((buttonEl) => {
        buttonEl.dataset.ftMounted = '1';
        buttonEl.addEventListener('click', () => {
            const action = buttonEl.dataset.ftSessionAction || '';
            const sessionId = buttonEl.dataset.ftSessionId || '';
            const campaignId = buttonEl.dataset.ftCampaignId || '';
            if (action === 'confirm-next') {
                const target = document.getElementById(buttonEl.dataset.ftTarget || '');
                buttonEl.style.display = 'none';
                if (target) {
                    target.classList.remove('d-none');
                    target.style.display = '';
                }
                return;
            }
            if (action === 'delete-session') {
                void queryData(resolveUrl('options/tools/campaign_sessions/delete_campaign_session_inc.php'), buttonEl.dataset.ftTarget || '', {
                    campaign_session_id: sessionId,
                });
                return;
            }
            if (action === 'skip-session' || action === 'rejoin-session') {
                const skip = document.getElementById(`btn_skip_campaign_session_${sessionId}`) as HTMLElement | null;
                const rejoin = document.getElementById(`btn_rejoin_campaign_session_${sessionId}`) as HTMLElement | null;
                const skipping = action === 'skip-session';
                if (skip) skip.style.display = skipping ? 'none' : '';
                if (rejoin) rejoin.style.display = skipping ? '' : 'none';
                void queryData(
                    resolveUrl(skipping
                        ? 'options/tools/campaign_sessions_user_interactions/skip_campaign_session.php'
                        : 'options/tools/campaign_sessions_user_interactions/rejoin_campaign_session.php'),
                    `div_refresh_${sessionId}`,
                    { campaign_id: campaignId, campaign_session_id: sessionId },
                );
                return;
            }
            if (action === 'reschedule-session') {
                void (async () => {
                    await queryDataInputs(resolveUrl('options/tools/campaign_sessions/reschedule_campaign_session_inc.php'), 'div_reschedule_feedback', {
                        campaign_session_id: 'input_hidden_reschedule_campaign_session_id',
                        campaign_session_date: 'campaign_session_reschedule_date',
                        campaign_session_time: 'campaign_session_reschedule_time',
                    });
                    const el = document.getElementById('div_reschedule_feedback');
                    if (el && (el.textContent || '').trim().startsWith('Rescheduled')) {
                        location.reload();
                    }
                })();
            }
        });
    });
}

function td(row: HTMLTableRowElement, className = ''): HTMLTableCellElement {
    const cell = document.createElement('td');
    if (className) cell.className = className;
    row.appendChild(cell);
    return cell;
}

function button(parent: HTMLElement, id: string, className: string, iconId: string, label: string, shown: boolean, fn: () => void | Promise<void>): HTMLButtonElement {
    const b = document.createElement('button');
    b.id = id;
    b.type = 'button';
    b.className = className;
    b.style.display = shown ? '' : 'none';
    b.setAttribute('aria-label', label);
    b.innerHTML = icon(iconId, 20, 20, label);
    b.addEventListener('click', () => { void fn(); });
    parent.appendChild(b);
    return b;
}

function link(parent: HTMLElement, href: string, className: string, iconId: string, label: string): void {
    const a = document.createElement('a');
    a.href = href;
    a.className = className;
    a.setAttribute('aria-label', label);
    a.innerHTML = icon(iconId, 20, 20, label);
    parent.appendChild(a);
}

async function post(url: string, target: string, payload: Record<string, string>): Promise<void> {
    await queryData(resolveUrl(url), target, payload);
}

function togglePair(parent: HTMLElement, opts: {
    itemId: string;
    flag: boolean;
    onId: string;
    offId: string;
    onIcon: string;
    offIcon: string;
    onLabel: string;
    offLabel: string;
    onUrl: string;
    offUrl: string;
    target: string;
    payload: Record<string, string>;
}): void {
    const on = button(parent, opts.onId, 'btn btn-sm btn-danger m-1', opts.onIcon, opts.onLabel, !opts.flag, async () => {
        on.style.display = 'none';
        off.style.display = '';
        await post(opts.onUrl, opts.target, opts.payload);
    });
    const off = button(parent, opts.offId, 'btn btn-sm btn-warning m-1', opts.offIcon, opts.offLabel, opts.flag, async () => {
        off.style.display = 'none';
        on.style.display = '';
        await post(opts.offUrl, opts.target, opts.payload);
    });
    // queryData() writes the endpoint's reply into this element and throws before the fetch when it is missing.
    const response = document.createElement('div');
    response.id = opts.target;
    response.className = 'small';
    parent.appendChild(response);
}

function renderRow(p: CampaignSessionPlayerRow): HTMLTableRowElement {
    const row = document.createElement('tr');
    row.dataset.ftPlayerId = p.item_id;
    row.dataset.ftPlayerText = p.text || `${p.character_name} ${p.player_username}`.toLowerCase();

    td(row, 'text-center align-middle').textContent = String(p.index);
    const pic = td(row, 'text-center align-middle');
    const picWrap = document.createElement('div');
    picWrap.className = 'p-1';
    picWrap.innerHTML = rosterThumbnailHtml(p.thumbnail_inputs, p.can_manage_character);
    pic.appendChild(picWrap);

    const nameCell = td(row);
    const name = document.createElement('a');
    name.className = 'link-primary';
    name.href = p.character_url;
    name.textContent = p.character_name;
    nameCell.appendChild(name);

    const playerCell = td(row);
    const player = document.createElement('a');
    player.className = 'link-primary';
    player.href = p.player_url;
    player.textContent = p.player_username;
    playerCell.appendChild(player);

    const level = td(row, 'text-center');
    const badge = document.createElement('span');
    badge.className = 'h5 badge bg-dark border';
    badge.textContent = rosterComputedCell(p.client_computed, 'total_level');
    badge.dataset.ftCreatureStat = 'total_level';
    badge.dataset.ftCasterId = p.item_id;
    badge.dataset.ftStatComputed = p.client_computed == null ? '0' : '1';
    level.appendChild(badge);

    td(row, 'text-center').innerHTML = rosterClassIcons(p.client_creature ?? {});
    td(row, 'text-center').innerHTML = p.skipping ? icon('skipping_campaign_session') : icon('check');

    const actions = td(row, 'text-center');
    if (!p.can_manage_character) return row;
    const group = document.createElement('div');
    group.className = 'd-flex justify-content-center flex-wrap';
    actions.appendChild(group);

    link(group, p.edit_url, 'btn btn-sm btn-info m-1', 'edit_player_character', 'Edit character');

    const active = button(group, `btn_toggle_character_active_${p.item_id}`, 'btn btn-sm btn-primary m-1', 'player_character_active', 'Character is ACTIVE — click to set inactive', !p.inactive, async () => {
        // 2026-09-02: five players clicked this mid-lockup looking for "make me active" and were
        // marked inactive (camp, out of the party). The inactive direction now confirms.
        if (!window.confirm(`Set ${p.character_name} INACTIVE? An inactive character leaves the active party and is skipped by session and party moves. (To rejoin later, use "Mark character active".)`)) return;
        if (typeof openSideLog === 'function') openSideLog();
        await post('options/tools/player_characters/update/update_player_character_inactive_inc.php', 'div_sidelog_warning', { item_id: p.item_id, player_character_inactive: '1', csrf_token: p.csrf_token });
        active.style.display = 'none';
        inactive.style.display = '';
    });
    const inactive = button(group, `btn_toggle_character_inactive_${p.item_id}`, 'btn btn-sm btn-danger m-1', 'player_character_inactive', 'Mark character active', p.inactive, async () => {
        if (typeof openSideLog === 'function') openSideLog();
        await post('options/tools/player_characters/update/update_player_character_inactive_inc.php', 'div_sidelog_warning', { item_id: p.item_id, player_character_inactive: '0', csrf_token: p.csrf_token });
        active.style.display = '';
        inactive.style.display = 'none';
    });

    const common = { player_item_id: p.item_id, campaign_id: p.campaign_id, campaign_session_id: p.campaign_session_id, csrf_token: p.csrf_token };
    if (p.can_manage_attendance) {
        togglePair(group, {
            itemId: p.item_id,
            flag: p.late,
            onId: `btn_mark_player_character_late_${p.item_id}`,
            offId: `btn_mark_player_character_not_late_${p.item_id}`,
            onIcon: 'mark_player_character_late',
            offIcon: 'mark_player_character_not_late',
            onLabel: 'Mark late',
            offLabel: 'Clear late',
            onUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_late.php',
            offUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_not_late.php',
            target: `div_mark_player_character_late_${p.item_id}`,
            payload: common,
        });
        togglePair(group, {
            itemId: p.item_id,
            flag: p.no_show,
            onId: `btn_mark_player_character_no_show_${p.item_id}`,
            offId: `btn_mark_player_character_show_${p.item_id}`,
            onIcon: 'mark_player_character_no_show',
            offIcon: 'mark_player_character_as_show',
            onLabel: 'Mark no-show',
            offLabel: 'Mark show',
            onUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_no_show.php',
            offUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_show.php',
            target: `div_mark_player_character_no_show_${p.item_id}`,
            payload: common,
        });
        togglePair(group, {
            itemId: p.item_id,
            flag: p.left_early,
            onId: `btn_mark_player_character_left_early_${p.item_id}`,
            offId: `btn_mark_player_character_not_left_early_${p.item_id}`,
            onIcon: 'mark_player_character_left_early',
            offIcon: 'mark_player_character_not_left_early',
            onLabel: 'Mark left early',
            offLabel: 'Clear left early',
            onUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_left_early.php',
            offUrl: 'options/tools/campaign_sessions_user_interactions/mark_player_character_not_left_early.php',
            target: `div_mark_player_character_left_early_${p.item_id}`,
            payload: common,
        });
    }

    if (p.can_kick) {
        const kick = button(group, `btn_toggle_character_kick_${p.item_id}`, 'btn btn-sm btn-warning m-1', 'kick_player_character_from_campaign', 'Kick player', true, () => {
            kick.style.display = 'none';
            confirm.style.display = '';
        });
        const confirm = button(group, `btn_toggle_character_kick_check_${p.item_id}`, 'btn btn-sm btn-danger m-1', 'confirm_kick_player_character_from_campaign', 'Confirm kick', false, async () => {
            await post('options/tools/campaigns/update/update_kick_player_character_from_campaign_inc.php', 'div_sidelog_warning', { item_id: p.item_id, csrf_token: p.csrf_token });
            if (typeof openSideLog === 'function') openSideLog();
            row.remove();
        });
    }

    return row;
}

function build(container: HTMLElement, data: CatalogCampaignSessionPlayersResponse, searchInput: HTMLInputElement | null): void {
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'table-responsive-xxl';
    const table = document.createElement('table');
    table.className = 'ft-sortable';
    table.innerHTML = `<thead><tr>
        <th class="text-center ft-no-filter" style="width: 50px;">#</th>
        <th class="text-center ft-no-sort ft-no-filter" style="width: 100px;">${icon('picture')}</th>
        <th class="text-left">Character Name</th>
        <th class="text-left">Player</th>
        <th class="text-center" style="width: 80px;">${icon('level')}</th>
        <th class="text-center" style="width: 160px;">${icon('class')}</th>
        <th class="text-center" style="width: 80px;">${icon('skipping_campaign_session')}</th>
        <th class="text-center ft-no-sort ft-no-filter">${icon('settings')}</th>
    </tr></thead>`;
    const tbody = document.createElement('tbody');
    const rows = (data.players || []).map(renderRow);
    rows.forEach((r) => tbody.appendChild(r));
    table.appendChild(tbody);
    wrap.appendChild(table);
    container.appendChild(wrap);
    const empty = document.createElement('div');
    empty.className = 'text-center text-muted py-5';
    empty.textContent = 'No campaign players found.';
    container.appendChild(empty);
    const apply = () => {
        const q = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const r of rows) {
            if (!r.isConnected) continue;
            const show = !q || (r.dataset.ftPlayerText || '').includes(q);
            r.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        wrap.style.display = visible ? '' : 'none';
        empty.style.display = visible ? 'none' : '';
    };
    searchInput?.addEventListener('input', apply);
    (window as unknown as { ftMountPcSheet?: (root: ParentNode) => void }).ftMountPcSheet?.(container);
    (window as unknown as { ftMountThumbnails?: (root: Element) => void }).ftMountThumbnails?.(container);
    initTooltips(container);
    if (typeof window.ftSortableScan === 'function') window.ftSortableScan();
    apply();
}

export function mountCampaignSessionPlayers(root: HTMLElement, config: CatalogListConfig): void {
    wireSessionControls();
    const container = results(root);
    if (!container) return;
    const searchInput = config.searchInputId ? (document.getElementById(config.searchInputId) as HTMLInputElement | null) : null;
    void (async () => {
        const timer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
            const payload = parseCampaignRowsResponse(await res.text(), res.status, 'campaign-roster-v1');
            const data = campaignRosterView(payload);
            clearTimeout(timer);
            hideLoader(root);
            setStatus(root, '');
            build(container, data, searchInput);
        } catch (e) {
            clearTimeout(timer);
            hideLoader(root);
            console.error('[campaign-session-players] fetch/render failed', e);
            setStatus(root, e instanceof Error ? e.message : 'Could not load players. Please try again.');
        }
    })();
}
